import { createFileRoute } from '@tanstack/react-router';

import prisma from 'database';
import { Article, Feature } from 'database';
import _ from 'lodash';
import { useState } from 'react';
import { createServerFn } from '@tanstack/react-start';
import { TWButton } from '../../components/button';
import SuggestRecommendationModal from '../../components/modals/suggest-recommendation-modal';
import { useAuth } from 'shared/misc/hooks/useAuth';
import { DocumentStatus } from 'shared/__generated__/graphql';
import { useAnalytics } from 'shared/misc/providers/AnalyticsContext';

export type ArticleWithExtraData = Article & { document: Document | null; feature: Feature | null };

const getUnlockedArticles = createServerFn({ method: 'GET' }).handler(async () => {
  const articles = await prisma.article.findMany({
    orderBy: [
      {
        score: {
          sort: 'desc',
          nulls: 'last',
        },
      },
      {
        publishedAt: {
          sort: 'desc',
          nulls: 'last',
        },
      },
    ],
    where: {
      status: DocumentStatus.Published,
    },
    include: {
      feature: {
        select: {
          id: true,
          title: true,
          subtitle: true,
          score: true,
          guests: true,
        },
      },
      document: {
        select: {
          id: true,
          title: true,
          subtitle: true,
        },
      },
    },
  });

  return articles;
});

export const Route = createFileRoute('/suggest/')({
  component: RouteComponent,
  head: () => ({
    meta: [
      {
        title: 'Suggest | Perfectly Imperfect',
      },
    ],
  }),
  loader: async () => {
    const articles = await getUnlockedArticles();
    return articles;
  },
});

function RouteComponent() {
  const articles = Route.useLoaderData();
  const [searchQuery, setSearchQuery] = useState('');
  const [recOpen, setIsRecOpen] = useState(false);

  const escapedSearchQuery = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const queryRegex = RegExp(escapedSearchQuery, 'ig');

  const sortedArticles = _.sortBy(
    articles,
    (article) => article.score || article.feature?.score || 0,
  ).reverse();

  const isMatch = (article: ArticleWithExtraData) => {
    if (searchQuery == '') return true;
    if ((article.title || '').match(queryRegex)) {
      return true;
    }
    if (article.feature) {
      if (
        (article.feature.guests || []).some((guest) => {
          return (guest.name || '').match(queryRegex);
        })
      )
        return true;
    }
    return false;
  };

  const filteredArticles = searchQuery
    ? sortedArticles.filter((article) => isMatch(article))
    : sortedArticles;
  const auth = useAuth();
  const userSignedIn = auth.user ? true : false;
  const { trackEvent } = useAnalytics();

  return (
    <div
      style={{ backgroundImage: "url('https://files.pi.fyi/star-background.png')" }}
      className="sm:min-h-[calc(100vh-64px)] min-h-screen max-h-content mb-0 flex flex-col items-center justify-start px-4 py-8 bg-theme-backround"
    >
      <div className="flex-shrink-0 scale-y-150">
        <h1 className="uppercase typography-heading-xl text-center max-lg:text-6xl sm:my-8 ">
          SUGGEST
        </h1>
      </div>
      {recOpen ? (
        <SuggestRecommendationModal searchQuery={searchQuery} />
      ) : (
        <div className="w-full flex flex-col items-center flex-grow min-h-0">
          <div className="w-full max-w-5xl flex-shrink-0">
            <div className="my-8">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full p-6 text-2xl border-2 border-dashed rounded-none border-brand-highlight  uppercase focus:outline-none bg-brand-background"
                placeholder="type a name"
              />
            </div>
          </div>
          <div className="w-full max-w-5xl flex-grow min-h-0">
            {filteredArticles.length > 0 ? (
              <div className="border-brand-highlight h-full ">
                {filteredArticles.map((article: ArticleWithExtraData) => {
                  // let articleDate = article.publishedAt
                  //   ? new Date(article.publishedAt).toLocaleDateString('en-US', {
                  //       year: 'numeric',
                  //       month: 'long',
                  //       day: 'numeric',
                  //     })
                  //   : undefined;
                  let title =
                    article.title || article.document?.title || article.feature?.title || '';

                  try {
                    title = title
                      .replace(/#([0-9]{1,3}[:\s])/gi, '')
                      .trim()
                      .toUpperCase();
                  } catch (e) {
                    console.log(e);
                  }

                  return (
                    <div className="font-bold pb-6 text-left scale-y-150">
                      <a
                        onClick={() =>
                          trackEvent('Click', 'Suggest', {
                            urlSlug: article.urlSlug,
                          })
                        }
                        key={article.id}
                        href={`https://pi.fyi/p/${article.urlSlug}`}
                        className=" border-theme-title-background"
                      >
                        <span className="text-theme-main -ml-3 text-2xl px-3 hover:bg-brand-highlight hover:text-brand-background">
                          {title}
                        </span>
                        {/* <span className="text-theme-main -ml-3 text-lg px-3">{articleDate}</span> */}
                      </a>
                    </div>
                  );
                })}
              </div>
            ) : (
              //no results
              <div className="bg-brand-main uppercase px-4 py-6 text-center">
                <div>
                  <div className="text-brand-background text-xs sm:text-sm scale-y-150">
                    Lucky for us, you're here to tell us what you're into.
                  </div>
                  <div className="text-brand-background underline text-lg mt-6 sm:mt-3 line-clamp-2 sm:line-clamp-1 scale-y-150">
                    <button
                      onClick={() => setIsRecOpen(true)}
                      className="hover:text-brand-highlight uppercase underline"
                    >
                      {!userSignedIn && 'join PERFECTLY IMPERFECT to '}recommend {searchQuery}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <TWButton
        variant={'primary'}
        className="fixed bottom-0 right-0 m-6 "
        onClick={() => {
          document.body.scrollTop = 0;
          document.documentElement.scrollTop = 0;
        }}
      >
        Back to top
      </TWButton>
    </div>
  );
}
